import { isEmpty, escape } from 'lodash';
import {
  getPostCover,
  getCanonicalPostUrl,
  POST_EXCERPT_MAX_LENGTH,
  getImageUrl,
  getCategoryUrl,
  type AppConfig,
} from '@wix/communities-blog-client-common';
import getJsonSchemaPublisher from './get-json-schema-publisher';

const getDescription = (post: { excerpt?: string }) => {
  const text = post.excerpt || '';
  return text.slice(0, POST_EXCERPT_MAX_LENGTH);
};

const getImageSchema = (image: any) => ({
  '@type': 'ImageObject',
  height: image.height,
  width: image.width,
  url: getImageUrl({
    image,
    maxWidth: 1000,
    maxHeight: 1000,
    quality: 80,
    type: 'fit',
    format: 'jpg',
  }),
});

const getAuthorSchema = ({ name, image }: { name?: string; image?: any }) => {
  return {
    '@type': 'Person',
    name,
    ...(image
      ? {
          image: {
            '@type': 'ImageObject',
            url:
              typeof image === 'string'
                ? image
                : getImageUrl({
                    image,
                    maxWidth: 1000,
                    maxHeight: 1000,
                    quality: 80,
                    type: 'fit',
                    format: 'jpg',
                  }),
          },
        }
      : {}),
  };
};

const getArticleSection = (post: any, categoryMap: Record<string, any>) =>
  post.categoryIds
    .map((id: string) => categoryMap[id])
    .filter(Boolean)
    .map(({ label }: { label?: string }) => label)
    .join(', ');

const getPostSchema = ({
  post,
  categoryMap,
  postPageSectionUrl,
  publisher,
  multilingual,
}: {
  post: any;
  categoryMap: Record<string, any>;
  postPageSectionUrl: string;
  publisher?: any;
  multilingual?: any;
  imageHost?: any;
}) => {
  if (!publisher || isEmpty(post) || !post.owner) {
    return undefined;
  }
  const { shouldRender, imageMetadata } = getPostCover(post);
  const postUrl = getCanonicalPostUrl({
    post,
    postPageSectionUrl,
    multilingual,
  });
  const description = getDescription(post);
  const articleSection = getArticleSection(post, categoryMap);

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    author: getAuthorSchema(post.owner),
    articleSection,
    dateModified: post.lastPublishedDate,
    datePublished: post.firstPublishedDate,
    headline: escape(post.title),
    mainEntityOfPage: { '@id': postUrl, '@type': 'WebPage' },
    description: escape(description),
    url: postUrl,
    image: shouldRender && imageMetadata ? getImageSchema(imageMetadata) : {},
    publisher,
  };
};

const getPostsSchema = ({
  appConfig,
  publisher,
  posts,
  categoryMap,
  postPageSectionUrl,
  multilingual,
}: {
  appConfig: AppConfig;
  publisher?: any;
  posts: any[];
  categoryMap: Record<string, any>;
  postPageSectionUrl: string;
  multilingual?: any;
}) =>
  posts.map((post) =>
    getPostSchema({
      post,
      categoryMap,
      postPageSectionUrl,
      publisher,
      imageHost: appConfig.imageHost,
      multilingual,
    }),
  );

export const getCategorySchema = ({
  appConfig,
  schemaEnabled,
  siteProperties,
  sectionUrl,
  postPageSectionUrl,
  category,
  posts,
  categoryMap,
  multilingual,
}: {
  appConfig: AppConfig;
  schemaEnabled?: boolean;
  siteProperties?: any;
  sectionUrl: string;
  postPageSectionUrl: string;
  category: any;
  posts: any[];
  categoryMap: any;
  multilingual?: any;
}) => {
  const { categoryPath } = appConfig;
  const publisher =
    getJsonSchemaPublisher({
      isWP: appConfig.isWP,
      imageHost: appConfig.imageHost as string,
      siteProperties,
      isAmp: false,
      isSchemaEnabled: schemaEnabled,
    }) || {};

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    url: getCategoryUrl(sectionUrl, categoryPath as string, category.slug),
    headline: escape(category.label),
    description: escape(category.description),
    hasPart: getPostsSchema({
      appConfig,
      publisher,
      posts,
      categoryMap,
      postPageSectionUrl,
      multilingual,
    }),
    sameAs: [],
  };
};
